import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/dates";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw } from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
} from "recharts";

type LongJobMode = "all" | "exclude" | "only";

type OverlayRow = {
  active_date: string;
  iso_dow: number;
  dow_label: string;
  hour_of_day: number;

  actual_jobs: number;
  actual_vehicles: number;
  actual_drivers: number;
  actual_jobs_per_vehicle: number;
  actual_equiv_jobs: number;

  avg_jobs_1m: number | null;
  avg_jobs_3m: number | null;
  avg_jobs_6m: number | null;
  avg_jobs_12m: number | null;

  avg_vehicles_1m: number | null;
  avg_vehicles_3m: number | null;
  avg_vehicles_6m: number | null;
  avg_vehicles_12m: number | null;
};

type VehicleTypeRow = {
  active_date: string;
  hour_of_day: number;
  vehicle_type: string;
  active_jobs: number;
  active_vehicles: number;
  active_drivers: number;
  jobs_per_vehicle: number;
  equivalent_full_hour_jobs: number;
};

type PlannedShiftRow = {
  shift_date: string;
  driver_name: string;
  status: string;
  start_hhmm: string;
  end_hhmm: string;
  shift_type_code: string | null;
  counts_as_worked_hours: boolean | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDateStr: string | null;
  fleet?: string | null;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const NON_WORKING_CODES_SET = new Set([
  "LEAVE",
  "SICK",
  "UNAVAILABLE",
  "TIME_OFF",
  "TIMEOFF",
  "OFFICE",
]);

const formatHour = (hour: number) => {
  if (hour === 0) return "12a";
  if (hour < 12) return `${hour}a`;
  if (hour === 12) return "12p";
  return `${hour - 12}p`;
};

const safeNum = (value: number | null | undefined) => Number(value || 0);

// Parse "HH:MM" to float hours (e.g. "13:30" -> 13.5)
const hhmmToFloat = (hhmm: string | null | undefined): number | null => {
  if (!hhmm) return null;
  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr);
  const m = Number(mStr || 0);
  if (Number.isNaN(h)) return null;
  return h + m / 60;
};

const DayTrendOverlay: React.FC<Props> = ({
  open,
  onOpenChange,
  selectedDateStr,
  fleet = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [longJobMode, setLongJobMode] = useState<LongJobMode>("all");
  const [rows, setRows] = useState<OverlayRow[]>([]);
  const [vehicleTypeRows, setVehicleTypeRows] = useState<VehicleTypeRow[]>([]);
  const [distinctJobs, setDistinctJobs] = useState<number>(0);
  const [plannedShifts, setPlannedShifts] = useState<PlannedShiftRow[]>([]);


  const loadData = async () => {
    if (!selectedDateStr) return;

    setLoading(true);
    try {
      const [overlayRes, byTypeRes, distinctRes, plannedRes] = await Promise.all([
        supabase.rpc("rpc_taxicaller_day_overlay", {
          p_selected_date: selectedDateStr,
          p_fleet: fleet || null,
          p_long_job_mode: longJobMode,
        }),
        supabase.rpc("rpc_taxicaller_day_overlay_by_vehicle_type", {
          p_selected_date: selectedDateStr,
          p_fleet: fleet || null,
          p_long_job_mode: longJobMode,
        }),
        supabase.rpc("rpc_taxicaller_day_distinct_job_count", {
          p_selected_date: selectedDateStr,
          p_fleet: fleet || null,
          p_long_job_mode: longJobMode,
        }),
        supabase
          .from("vw_roster_shifts_enriched")
          .select(
            "shift_date, driver_name, status, start_hhmm, end_hhmm, shift_type_code, counts_as_worked_hours"
          )
          .eq("shift_date", selectedDateStr),
      ]);

      if (overlayRes.error) throw overlayRes.error;
      if (byTypeRes.error) throw byTypeRes.error;
      if (distinctRes.error) throw distinctRes.error;
      if (plannedRes.error) throw plannedRes.error;

      setRows((overlayRes.data || []) as OverlayRow[]);
      setVehicleTypeRows((byTypeRes.data || []) as VehicleTypeRow[]);
      const distinctData = (distinctRes.data || []) as Array<{
        distinct_jobs: number | string;
      }>;
      const first = distinctData[0];
      setDistinctJobs(first ? Number(first.distinct_jobs || 0) : 0);
      setPlannedShifts((plannedRes.data || []) as PlannedShiftRow[]);
    } catch (error) {
      console.error("Failed to load day overlay", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && selectedDateStr) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDateStr, fleet, longJobMode]);


  // Planned drivers per hour of day from the roster (working shifts only)
  const plannedDriversByHour = useMemo(() => {
    const byHour = new Array<number>(24).fill(0);

    const workingShifts = plannedShifts.filter((s) => {
      if (s.status === "cancelled") return false;
      const code = (s.shift_type_code || "").toString().trim().toUpperCase();
      if (NON_WORKING_CODES_SET.has(code)) return false;
      if (s.counts_as_worked_hours === false) return false;
      return true;
    });

    for (let hour = 0; hour < 24; hour++) {
      const driversThisHour = new Set<string>();
      for (const s of workingShifts) {
        const start = hhmmToFloat(s.start_hhmm);
        const end = hhmmToFloat(s.end_hhmm);
        if (start === null || end === null) continue;

        // If shift crosses midnight, treat end as end+24 when end <= start
        let effectiveEnd = end;
        if (end <= start) effectiveEnd = end + 24;

        // Count driver as "on" if the hour [hour, hour+1) overlaps the shift
        if (hour + 1 > start && hour < effectiveEnd) {
          driversThisHour.add(s.driver_name);
        } else if (end <= start) {
          // Overflow into next-day hours — treat hour+24 as candidate
          if (hour + 25 > start && hour + 24 < effectiveEnd) {
            driversThisHour.add(s.driver_name);
          }
        }
      }
      byHour[hour] = driversThisHour.size;
    }

    return byHour;
  }, [plannedShifts]);

  const plannedDriversToday = useMemo(() => {
    const set = new Set<string>();
    for (const s of plannedShifts) {
      if (s.status === "cancelled") continue;
      const code = (s.shift_type_code || "").toString().trim().toUpperCase();
      if (NON_WORKING_CODES_SET.has(code)) continue;
      if (s.counts_as_worked_hours === false) continue;
      set.add(s.driver_name);
    }
    return set.size;
  }, [plannedShifts]);

  const chartData = useMemo(() => {
    const map = new Map<number, any>();

    for (const hour of HOURS) {
      map.set(hour, {
        hour_label: formatHour(hour),
        actual_jobs: 0,
        actual_vehicles: 0,
        avg_jobs_1m: 0,
        avg_jobs_3m: 0,
        avg_jobs_6m: 0,
        avg_jobs_12m: 0,
        planned_drivers: plannedDriversByHour[hour] || 0,
      });
    }

    for (const row of rows) {
      const target = map.get(row.hour_of_day);
      if (!target) continue;

      target.actual_jobs = safeNum(row.actual_jobs);
      target.actual_vehicles = safeNum(row.actual_vehicles);
      target.avg_jobs_1m = safeNum(row.avg_jobs_1m);
      target.avg_jobs_3m = safeNum(row.avg_jobs_3m);
      target.avg_jobs_6m = safeNum(row.avg_jobs_6m);
      target.avg_jobs_12m = safeNum(row.avg_jobs_12m);
    }

    return HOURS.map((hour) => map.get(hour));
  }, [rows, plannedDriversByHour]);

  const groupedVehicleTypes = useMemo(() => {
    const buckets = new Map<string, VehicleTypeRow[]>();
    for (const row of vehicleTypeRows) {
      const key = row.vehicle_type || "Unknown";
      const existing = buckets.get(key) || [];
      existing.push(row);
      buckets.set(key, existing);
    }
    return Array.from(buckets.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [vehicleTypeRows]);

  const totals = useMemo(() => {
    let totalJobs = 0;
    let peakVehicles = 0;
    let peakHour = 0;
    let peakJobs = 0;
    let peakAvg = 0;

    for (const row of rows) {
      totalJobs += safeNum(row.actual_jobs);
      if (safeNum(row.actual_vehicles) > peakVehicles) {
        peakVehicles = safeNum(row.actual_vehicles);
      }
      if (safeNum(row.actual_jobs) > peakJobs) {
        peakJobs = safeNum(row.actual_jobs);
        peakHour = row.hour_of_day;
      }

      const bestAvg = Math.max(
        safeNum(row.avg_jobs_1m),
        safeNum(row.avg_jobs_3m),
        safeNum(row.avg_jobs_6m),
        safeNum(row.avg_jobs_12m)
      );
      if (bestAvg > peakAvg) peakAvg = bestAvg;
    }

    return { totalJobs, peakVehicles, peakHour, peakJobs, peakAvg };
  }, [rows]);

  // Cover gap warnings: hours where expected jobs (best avg) notably exceed planned drivers
  const coverageGaps = useMemo(() => {
    const gaps: Array<{
      hour: number;
      expected: number;
      planned: number;
      shortfall: number;
    }> = [];

    for (const row of rows) {
      const expected = Math.max(
        safeNum(row.avg_jobs_1m),
        safeNum(row.avg_jobs_3m),
        safeNum(row.avg_jobs_6m),
        safeNum(row.avg_jobs_12m)
      );
      const planned = plannedDriversByHour[row.hour_of_day] || 0;

      // Rule of thumb: if avg expected jobs/hour > planned drivers by 2+, flag it
      if (expected > 0 && expected >= planned + 2) {
        gaps.push({
          hour: row.hour_of_day,
          expected: Math.round(expected * 10) / 10,
          planned,
          shortfall: Math.round((expected - planned) * 10) / 10,
        });
      }
    }

    return gaps.sort((a, b) => b.shortfall - a.shortfall);
  }, [rows, plannedDriversByHour]);

  const longJobOptions: { value: LongJobMode; label: string }[] = [
    { value: "all", label: "All Jobs" },
    { value: "exclude", label: "Exclude Long" },
    { value: "only", label: "Long Only" },
  ];

  const selectedDate = selectedDateStr
    ? new Date(`${selectedDateStr}T00:00:00`)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl bg-gray-900 border-gray-700 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Demand Trends —{" "}
            {selectedDate
              ? `${format(selectedDate, "EEEE")} ${formatDate(selectedDate)}`
              : "—"}
          </DialogTitle>
        </DialogHeader>
        <div className="w-full space-y-4 mt-2">
          {/* Controls */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Long Job Mode
              </label>
              <div className="flex gap-1">
                {longJobOptions.map((opt) => (
                  <Button
                    key={opt.value}
                    variant={longJobMode === opt.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLongJobMode(opt.value)}
                    className={
                      longJobMode === opt.value
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700"
                    }
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            {fleet && (
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Fleet
                </label>
                <Badge className="bg-gray-700 text-gray-100">{fleet}</Badge>
              </div>
            )}

            <div className="ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                disabled={loading}
                className="bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            </div>
          </div>

          {/* Summary badges */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase">Total Jobs</div>
              <div className="text-2xl font-bold">{distinctJobs}</div>
              <div className="text-xs text-gray-500 mt-1">Distinct job IDs</div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase">
                Active Job-Hours
              </div>
              <div className="text-2xl font-bold">{totals.totalJobs}</div>
              <div className="text-xs text-gray-500 mt-1">
                Sum of hourly activity
              </div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase">Peak Hour</div>
              <div className="text-2xl font-bold">
                {formatHour(totals.peakHour)}
              </div>
              <div className="text-xs text-gray-400">
                {totals.peakJobs} jobs
              </div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase">
                Peak Vehicles
              </div>
              <div className="text-2xl font-bold">{totals.peakVehicles}</div>
            </div>
            <div className="bg-amber-950/40 border border-amber-700/50 rounded-lg p-3">
              <div className="text-xs text-amber-300 uppercase">
                Planned Drivers
              </div>
              <div className="text-2xl font-bold text-amber-200">
                {plannedDriversToday}
              </div>
              <div className="text-xs text-amber-400/80 mt-1">
                Rostered for this day
              </div>
            </div>
          </div>

          {/* Coverage gaps callout */}
          {coverageGaps.length > 0 && (
            <div className="bg-amber-950/30 border border-amber-700/50 rounded-lg p-3">
              <div className="text-sm font-semibold text-amber-300 mb-2">
                Possible coverage gaps vs historical demand
              </div>
              <div className="flex flex-wrap gap-2">
                {coverageGaps.slice(0, 8).map((g) => (
                  <Badge
                    key={g.hour}
                    variant="outline"
                    className="border-amber-600/60 text-amber-200 bg-amber-900/20"
                  >
                    {formatHour(g.hour)} — need ~{g.expected}, have {g.planned}{" "}
                    (short {g.shortfall})
                  </Badge>
                ))}
              </div>
              <div className="text-[11px] text-amber-400/70 mt-2">
                Based on best historical average (1m / 3m / 6m / 12m) vs
                currently planned drivers on shift at that hour. Flagged when
                expected exceeds planned by 2+.
              </div>
            </div>
          )}

          {/* Chart */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-sm font-semibold mb-3 text-gray-200">
              Historical Averages &amp; Planned Driver Coverage
            </div>
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer>
                <ComposedChart data={chartData}>
                  <CartesianGrid stroke="#374151" strokeDasharray="3 3" />
                  <XAxis dataKey="hour_label" stroke="#9CA3AF" fontSize={12} />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: "#111827",
                      border: "1px solid #374151",
                      borderRadius: 6,
                      color: "#F9FAFB",
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="planned_drivers"
                    name="Planned Drivers"
                    fill="#F59E0B"
                    fillOpacity={0.55}
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_jobs_1m"
                    name="1m Avg"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_jobs_3m"
                    name="3m Avg"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_jobs_6m"
                    name="6m Avg"
                    stroke="#EF4444"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_jobs_12m"
                    name="12m Avg"
                    stroke="#A855F7"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] text-gray-500 mt-2">
              Amber bars = drivers rostered on shift each hour. Lines = expected
              jobs/hour based on historical averages. Use this to size your
              cover for peak times. For retrospective day/week/month actuals,
              use the <span className="text-gray-300 font-medium">Actual
              Performance</span> tab on the Demand page.
            </div>
          </div>

          {/* Vehicle types breakdown */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-sm font-semibold mb-3 text-gray-200">
              Vehicle Type Breakdown
            </div>
            {groupedVehicleTypes.length === 0 ? (
              <div className="text-sm text-gray-400">
                No vehicle type data available for this date.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="py-2 pr-4">Vehicle Type</th>
                      <th className="py-2 pr-4 text-right">Total Jobs</th>
                      <th className="py-2 pr-4 text-right">Peak Vehicles</th>
                      <th className="py-2 pr-4 text-right">Equivalent Jobs</th>
                      <th className="py-2 pr-4 text-right">
                        Avg Jobs / Vehicle
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedVehicleTypes.map(([type, typeRows]) => {
                      const totalJobs = typeRows.reduce(
                        (sum, r) => sum + safeNum(r.active_jobs),
                        0
                      );
                      const peakVehicles = typeRows.reduce(
                        (max, r) => Math.max(max, safeNum(r.active_vehicles)),
                        0
                      );
                      const equivJobs = typeRows.reduce(
                        (sum, r) => sum + safeNum(r.equivalent_full_hour_jobs),
                        0
                      );
                      const avgJobsPerVehicle =
                        typeRows.length > 0
                          ? typeRows.reduce(
                              (sum, r) => sum + safeNum(r.jobs_per_vehicle),
                              0
                            ) / typeRows.length
                          : 0;

                      return (
                        <tr
                          key={type}
                          className="border-b border-gray-700/50 hover:bg-gray-700/30"
                        >
                          <td className="py-2 pr-4 font-medium">{type}</td>
                          <td className="py-2 pr-4 text-right">{totalJobs}</td>
                          <td className="py-2 pr-4 text-right">
                            {peakVehicles}
                          </td>
                          <td className="py-2 pr-4 text-right">
                            {equivJobs.toFixed(1)}
                          </td>
                          <td className="py-2 pr-4 text-right">
                            {avgJobsPerVehicle.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DayTrendOverlay;
